import { getShareableShoppingCart } from "@/api/rest/checkoutApi";
import * as CheckoutAPI from "@/api/rest/checkoutSimplifiedApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setAllCarts, setCartItems } from "@/redux/reducers/checkout";
import {
  fetchProductMetadataWithGraphQL,
  getActiveCarts
} from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { useApolloClient } from "@apollo/client";
import { AnalyticsTrackingEvent, ShoppingCartItem } from "@brandclub/common-ui";
import isEqual from "lodash/isEqual";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo, useRef, useState
} from "react";
import type {
  Address,
  Buyer, CheckoutSession,
  CheckoutSessionWithOrder, Item
} from "./types";

type ProductMetadata = {
  stacklineSku: string;
  retailerSku: string;
  title: string;
  retailPrice: number;
  image: string;
  [key: string]: any;
};

type CheckoutSimplifiedUIContextType = {
  // Session state
  session: CheckoutSession | CheckoutSessionWithOrder | null;
  sessionLoading: boolean;
  sessionError: string | null;

  // Items loading state
  itemsLoading: boolean;

  // Product metadata
  productMetadata: Record<string, ProductMetadata>;

  // Section-specific loading states
  updatingBuyer: boolean;
  updatingAddress: boolean;
  updatingFulfillment: boolean;

  // Validation state
  contactHasErrors: boolean;
  setContactHasErrors: (hasErrors: boolean) => void;
  shippingAddressHasErrors: boolean;
  setShippingAddressHasErrors: (hasErrors: boolean) => void;

  // Session actions
  updateBuyer: (buyer: Buyer) => Promise<void>;
  updateAddress: (address: Address) => Promise<void>;
  selectFulfillmentOption: (optionId: string) => Promise<void>;
  completeCheckout: (
    paymentMethodId: string,
    billingAddress?: Address
  ) => Promise<CheckoutSessionWithOrder>;
  rememberSession: (newUserId: string) => Promise<void>;

  // Payment submission
  triggerPaymentSubmission: (() => void) | null;
  setTriggerPaymentSubmission: (trigger: (() => void) | null) => void;

  // Legacy support
  itemsToCheckout: any[];
  isLoading: boolean;
  clearCart: () => void;
};

const CheckoutSimplifiedUIContext =
  createContext<CheckoutSimplifiedUIContextType | null>(null);

interface CheckoutSimplifiedUIProviderProps {
  children: ReactNode;
}

export const CheckoutSimplifiedUIProvider = ({
  children,
}: CheckoutSimplifiedUIProviderProps) => {
  const dispatch = useAppDispatch();
  const apolloClient = useApolloClient();
  const userProfile = useAppSelector((state) => state.userProfile);
  const carts = useAppSelector(getAllCarts, isEqual);
  const cartItems = useAppSelector((state) => state.checkout?.cartItems);
  const [trackAction] = useTrackActions();

  // Session state
  const [session, setSession] =
    useState<CheckoutSession | CheckoutSessionWithOrder | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Items loading state
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loadedItems, setLoadedItems] = useState<Item[]>([]);
  const [productMetadata, setProductMetadata] = useState<
    Record<string, ProductMetadata>
  >({});

  // Section-specific loading states
  const [updatingBuyer, setUpdatingBuyer] = useState(false);
  const [updatingAddress, setUpdatingAddress] = useState(false);
  const [updatingFulfillment, setUpdatingFulfillment] = useState(false);

  // Validation state
  const [contactHasErrors, setContactHasErrors] = useState(false);
  const [shippingAddressHasErrors, setShippingAddressHasErrors] = useState(false);

  // Polling ref for payment processing
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track if we've loaded metadata for the current session
  const metadataLoadedForSessionRef = useRef<string | null>(null);

  // Track if we've tracked CHECKOUT_INTENT_CREATED for this session
  const hasTrackedIntentCreatedRef = useRef<string | null>(null);

  // Track if we're currently loading/creating a session to prevent race conditions
  const isLoadingSessionRef = useRef(false);

  // Payment submission trigger
  const [triggerPaymentSubmission, setTriggerPaymentSubmission] =
    useState<(() => void) | null>(null);

  // Memoize URL parameters to prevent infinite re-renders
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      sessionId: searchParams.get("sessionId"),
      retailerId: parseInt(searchParams.get("retailerId") || "0", 10),
      shareShoppingCartId: searchParams.get("shareShoppingCartId"),
      legacyRetailerSku: searchParams.get("retailerSku"),
      legacyQuantity: parseInt(searchParams.get("quantity") || "1", 10),
      searchParams,
    };
  }, []);

  const {
    sessionId,
    retailerId,
    shareShoppingCartId,
    legacyRetailerSku,
    legacyQuantity,
  } = urlParams;

  // Parse items from URL params
  const itemsFromUrlParams = useMemo(() => {
    const items: Array<{ sku: string; qty: number }> = [];
    let index = 0;

    // Try new format first
    while (urlParams.searchParams.has(`items[${index}][sku]`)) {
      const sku = urlParams.searchParams.get(`items[${index}][sku]`);
      const qty = urlParams.searchParams.get(`items[${index}][qty]`);

      if (sku) {
        items.push({
          sku,
          qty: parseInt(qty || "1", 10),
        });
      }
      index++;
    }

    // Backwards compatible: single item
    if (items.length === 0 && legacyRetailerSku) {
      items.push({ sku: legacyRetailerSku, qty: legacyQuantity });
    }

    return items;
  }, [urlParams.searchParams, legacyRetailerSku, legacyQuantity]);

  const hasUrlParamsForItems = retailerId && itemsFromUrlParams.length > 0;

  const [shareableShoppingCart, setShareableShoppingCart] =
    useState<{
      items: ShoppingCartItem[];
      retailerId: number;
    } | null>(null);

  // Load shareable shopping cart if provided
  useEffect(() => {
    if (shareShoppingCartId) {
      getShareableShoppingCart({ shoppingCartId: shareShoppingCartId }).then(
        ({ items }) => {
          setShareableShoppingCart({ items, retailerId: items[0].retailerId });
        }
      );
    }
  }, [shareShoppingCartId]);

  // Load items from URL params or cart (skip if sessionId exists in URL)
  useEffect(() => {
    // If session ID is in URL, we'll load the session directly
    if (sessionId) {
      return;
    }

    const loadItems = async () => {
      setItemsLoading(true);
      try {
        let items: Item[] = [];

        // Priority 1: URL parameters
        if (hasUrlParamsForItems) {
          const products = await fetchProductMetadataWithGraphQL({
            apolloClient,
            stacklineSkus: [],
            retailerSkus: itemsFromUrlParams.map((item) => item.sku),
            retailerIds: itemsFromUrlParams.map(() => retailerId),
            isAuthenticated: !!userProfile?.userId,
          });

          items = products.map((product, idx) => ({
            id: product.stacklineSku,
            quantity: itemsFromUrlParams[idx]?.qty || 1,
          }));

          // Store product metadata
          const metadata: Record<string, ProductMetadata> = {};
          products.forEach((product) => {
            metadata[product.stacklineSku] = {
              stacklineSku: product.stacklineSku,
              title: product.title || "Product",
              retailPrice: product.retailPrice || 0,
              image: product.imageUrls?.[0] || "",
              ...product,
            };
          });
          setProductMetadata(metadata);
        }
        // Priority 2: Shared shopping cart
        else if (shareShoppingCartId && shareableShoppingCart) {
          items = shareableShoppingCart.items.map((item) => ({
            id: item.stacklineSku,
            quantity: item.quantity,
          }));

          const products = await fetchProductMetadataWithGraphQL({
            apolloClient,
            stacklineSkus: [],
            retailerSkus: shareableShoppingCart.items.map(
              (item) => item.retailerSku
            ),
            retailerIds: [shareableShoppingCart.retailerId],
            isAuthenticated: !!userProfile?.userId,
          });

          // Store product metadata
          const metadata: Record<string, ProductMetadata> = {};
          products.forEach((product) => {
            metadata[product.stacklineSku] = {
              stacklineSku: product.stacklineSku,
              title: product.title || "Product",
              retailPrice: product.retailPrice || 0,
              image: product.imageUrls?.[0] || "",
              ...product,
            };
          });
          setProductMetadata(metadata);
        }
        // Priority 3: User's existing cart
        else if (carts.length > 0) {
          let shoppingCartSelected: any;
          if (carts.length === 1) {
            shoppingCartSelected = carts[0];
          } else if (retailerId) {
            shoppingCartSelected =
              carts.find((cart) => cart.retailerId === retailerId) || carts[0];
          }
          if (!shoppingCartSelected && carts.length > 0) {
            shoppingCartSelected = carts[0];
          }
          if (shoppingCartSelected) {
            items = shoppingCartSelected.items.map(
              (item: ShoppingCartItem) => ({
                id: item.stacklineSku,
                quantity: item.quantity,
              })
            );

            // Store product metadata from cart items
            const products = await fetchProductMetadataWithGraphQL({
              apolloClient,
              stacklineSkus: [],
              retailerSkus: shoppingCartSelected.items.map(
                (item: ShoppingCartItem) => item.retailerSku
              ),
              retailerIds: [shoppingCartSelected.retailerId],
              isAuthenticated: !!userProfile?.userId,
            });

            // Store product metadata
            const metadata: Record<string, ProductMetadata> = {};
            products.forEach((product) => {
              metadata[product.stacklineSku] = {
                stacklineSku: product.stacklineSku,
                title: product.title || "Product",
                retailPrice: product.retailPrice || 0,
                image: product.imageUrls?.[0] || "",
                ...product,
              };
            });
            setProductMetadata(metadata);
          }
        }

        setLoadedItems(items);
      } catch (error) {
        console.error("Error loading items:", error);
        setSessionError("Failed to load items");
      } finally {
        setItemsLoading(false);
      }
    };

    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionId,
    apolloClient,
    hasUrlParamsForItems,
    retailerId,
    itemsFromUrlParams,
    userProfile?.userId,
    shareShoppingCartId,
    shareableShoppingCart,
    carts,
  ]);

  // Load existing session from URL or create new session
  useEffect(() => {
    const loadOrCreateSession = async () => {
      if (session || itemsLoading || isLoadingSessionRef.current) {
        return;
      }

      // Priority 1: Load existing session from URL
      if (sessionId) {
        isLoadingSessionRef.current = true;
        setSessionLoading(true);
        setSessionError(null);

        try {
          const existingSession = await CheckoutAPI.getCheckoutSession(
            sessionId
          );
          setSession(existingSession);

          // Fetch product metadata for the session's line items if not already loaded
          if (
            existingSession.line_items.length > 0 &&
            metadataLoadedForSessionRef.current !== sessionId
          ) {
            const stacklineSkus = existingSession.line_items.map(
              (item) => item.item.id
            );
            const products = await fetchProductMetadataWithGraphQL({
              apolloClient,
              stacklineSkus,
              retailerSkus: [],
              isAuthenticated: !!userProfile?.userId,
            });

            const metadata: Record<string, ProductMetadata> = {};
            products.forEach((product) => {
              metadata[product.stacklineSku] = {
                stacklineSku: product.stacklineSku,
                retailerSku: product.retailerSku,
                title: product.title || "Product",
                retailPrice: product.retailPrice || 0,
                image: product.imageUrls?.[0] || "",
                ...product,
              };
            });
            setProductMetadata(metadata);
            metadataLoadedForSessionRef.current = sessionId;
          }
        } catch (error) {
          console.error("Error loading session:", error);
          setSessionError(CheckoutAPI.getErrorMessage(error));
        } finally {
          isLoadingSessionRef.current = false;
          setSessionLoading(false);
        }
        return;
      }

      // Priority 2: Create new session if items are loaded
      if (loadedItems.length === 0) {
        return;
      }

      isLoadingSessionRef.current = true;
      setSessionLoading(true);
      setSessionError(null);

      try {
        const buyer: Buyer | undefined = userProfile?.userId
          ? {
            first_name: (userProfile as any).firstName || "",
            last_name: (userProfile as any).lastName || "",
            email: userProfile.email || "",
            phone_number: (userProfile as any).phoneNumber,
          }
          : undefined;

        const newSession = await CheckoutAPI.createCheckoutSession({
          items: loadedItems,
          buyer,
        });

        setSession(newSession);

        // Update URL with session ID and remove cart params
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("sessionId", newSession.id);
        // Remove cart-related params
        newUrl.searchParams.delete("retailerId");
        newUrl.searchParams.delete("retailerSku");
        newUrl.searchParams.delete("stacklineSku");
        newUrl.searchParams.delete("quantity");
        newUrl.searchParams.delete("shareShoppingCartId");
        newUrl.searchParams.delete("action");
        // Remove items array params
        let index = 0;
        while (newUrl.searchParams.has(`items[${index}][sku]`)) {
          newUrl.searchParams.delete(`items[${index}][sku]`);
          newUrl.searchParams.delete(`items[${index}][qty]`);
          index++;
        }
        window.history.replaceState({}, "", newUrl.toString());
      } catch (error) {
        console.error("Error creating checkout session:", error);
        setSessionError(CheckoutAPI.getErrorMessage(error));
      } finally {
        isLoadingSessionRef.current = false;
        setSessionLoading(false);
      }
    };

    loadOrCreateSession();
  }, [
    loadedItems,
    session,
    itemsLoading,
    userProfile,
    sessionId,
    apolloClient,
  ]);

  // Track CHECKOUT_INTENT_CREATED when session is first created/loaded with metadata
  useEffect(() => {
    if (!session?.id || hasTrackedIntentCreatedRef.current === session.id) {
      return;
    }

    // Only track if session was created within last 30 seconds (to avoid duplicate events on reload)
    if (session.created_at) {
      try {
        const createdTime = new Date(session.created_at);
        const now = new Date();
        const timeDifferenceInSeconds = (now.getTime() - createdTime.getTime()) / 1000;
        if (timeDifferenceInSeconds > 30) {
          // Session is too old, don't track to avoid duplicates
          hasTrackedIntentCreatedRef.current = session.id; // Mark as tracked to prevent future attempts
          return;
        }
      } catch (error) {
        console.error("Error tracking CHECKOUT_INTENT_CREATED:", error);
      }
    }

    // Mark as tracked for this session
    hasTrackedIntentCreatedRef.current = session.id;

    // Prepare checkout items with metadata for tracking
    const checkoutItems = session.line_items.map((item) => {
      const metadata = productMetadata[item.item.id];
      return {
        stacklineSku: item.item.id,
        retailerId: metadata?.retailerId,
        retailerSku: metadata?.retailerSku,
        retailerParentSku: metadata?.retailerParentSku,
        title: metadata?.title,
        brandId: metadata?.brandId,
        brandName: metadata?.brandName,
        categoryId: metadata?.categoryId,
        categoryName: metadata?.categoryName,
        subCategoryId: metadata?.subCategoryId,
        subCategoryName: metadata?.subCategoryName,
        quantity: item.item.quantity,
        retailPrice: metadata?.retailPrice || item.base_amount,
        pixels: metadata?.pixels,
      };
    });

    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_INTENT_CREATED, {
      checkoutIntentId: session.id,
      checkoutItems,
      itemsCount: session.line_items.length,
    });
  }, [session?.id, session?.line_items, productMetadata, trackAction, session?.created_at]);

  // Poll session status during payment processing
  useEffect(() => {
    if (session?.status === "in_progress") {
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const updatedSession = await CheckoutAPI.getCheckoutSession(
            session.id
          );
          setSession(updatedSession);

          // Stop polling when payment completes or fails
          if (updatedSession.status !== "in_progress") {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } catch (error) {
          console.error("Error polling session status:", error);
        }
      }, 2000); // Poll every 2 seconds

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [session?.status, session?.id]);

  // Update buyer info
  const updateBuyer = useCallback(
    async (buyer: Buyer) => {
      if (!session) return;

      setUpdatingBuyer(true);
      setSessionError(null);

      try {
        const updatedSession = await CheckoutAPI.updateCheckoutSession(
          session.id,
          { buyer }
        );
        setSession(updatedSession);
      } catch (error) {
        console.error("Error updating buyer:", error);
        setSessionError(CheckoutAPI.getErrorMessage(error));
        throw error;
      } finally {
        setUpdatingBuyer(false);
      }
    },
    [session]
  );

  // Update address
  const updateAddress = useCallback(
    async (address: Address) => {
      if (!session) return;

      setUpdatingAddress(true);
      setSessionError(null);

      try {
        const updatedSession = await CheckoutAPI.updateCheckoutSession(
          session.id,
          { fulfillment_address: address }
        );
        setSession(updatedSession);
      } catch (error) {
        console.error("Error updating address:", error);
        setSessionError(CheckoutAPI.getErrorMessage(error));
        throw error;
      } finally {
        setUpdatingAddress(false);
      }
    },
    [session]
  );

  // Select fulfillment option
  const selectFulfillmentOption = useCallback(
    async (optionId: string) => {
      if (!session) return;

      setUpdatingFulfillment(true);
      setSessionError(null);

      try {
        const updatedSession = await CheckoutAPI.updateCheckoutSession(
          session.id,
          { fulfillment_option_id: optionId }
        );
        setSession(updatedSession);
      } catch (error) {
        console.error("Error selecting fulfillment option:", error);
        setSessionError(CheckoutAPI.getErrorMessage(error));
        throw error;
      } finally {
        setUpdatingFulfillment(false);
      }
    },
    [session]
  );

  // Complete checkout
  const completeCheckout = useCallback(
    async (
      paymentMethodId: string,
      billingAddress?: Address
    ): Promise<CheckoutSessionWithOrder> => {
      if (!session) {
        throw new Error("No active session");
      }
      isLoadingSessionRef.current = true;
      setSessionLoading(true);
      setSessionError(null);

      try {
        const completedSession = await CheckoutAPI.completeCheckoutSession(
          session.id,
          {
            payment_data: {
              payment_method_id: paymentMethodId,
              provider: "stripe",
              billing_address: billingAddress,
            },
            buyer: session.buyer,
          }
        );

        setSession(completedSession);
        return completedSession;
      } catch (error) {
        console.error("Error completing checkout:", error);
        setSessionError(CheckoutAPI.getErrorMessage(error));
        throw error;
      } finally {
        isLoadingSessionRef.current = false;
        setSessionLoading(false);
      }
    },
    [session]
  );

  // Remember session - associate session with newly signed-in user
  const rememberSession = useCallback(
    async (newUserId: string) => {
      if (!session) {
        throw new Error("No active session");
      }

      // Only call API if session user_id differs from new user
      if (session.user_id !== newUserId) {
        setSessionError(null);

        try {
          console.log(
            `Session user_id (${session.user_id}) differs from signed-in user (${newUserId}). Calling rememberCheckoutSession...`
          );
          const updatedSession = await CheckoutAPI.rememberCheckoutSession(
            session.id
          );
          setSession(updatedSession);
        } catch (error) {
          console.error("Error remembering checkout session:", error);
          setSessionError(CheckoutAPI.getErrorMessage(error));
          throw error;
        }
      }
    },
    [session]
  );

  // Clear cart (legacy support)
  const clearCart = useCallback(() => {
    const retailerIdToClear = shareableShoppingCart?.retailerId;
    if (!retailerIdToClear) {
      return;
    }
    const newAllCarts = (carts || []).map((cart) =>
      cart.retailerId === retailerIdToClear
        ? { ...cart, items: [], totalItems: 0 }
        : cart
    );
    const newCartItems = (cartItems || []).filter(
      (item) => item.retailerId !== retailerIdToClear
    );
    dispatch(setAllCarts(newAllCarts as any));
    dispatch(setCartItems(newCartItems as any));
    dispatch(getActiveCarts());
  }, [shareableShoppingCart?.retailerId, carts, cartItems, dispatch]);

  // Fetch active carts on mount
  useEffect(() => {
    dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.userId]);

  // Legacy itemsToCheckout for backwards compatibility
  const itemsToCheckout = useMemo(() => {
    return loadedItems.map((item) => ({
      id: item.id,
      quantity: item.quantity,
    }));
  }, [loadedItems]);

  return (
    <CheckoutSimplifiedUIContext.Provider
      value={{
        session,
        sessionLoading,
        sessionError,
        itemsLoading,
        productMetadata,
        updatingBuyer,
        updatingAddress,
        updatingFulfillment,
        contactHasErrors,
        setContactHasErrors,
        shippingAddressHasErrors,
        setShippingAddressHasErrors,
        updateBuyer,
        updateAddress,
        selectFulfillmentOption,
        completeCheckout,
        rememberSession,
        triggerPaymentSubmission,
        setTriggerPaymentSubmission,
        // Legacy support
        itemsToCheckout,
        isLoading: itemsLoading || sessionLoading,
        clearCart,
      }}
    >
      {children}
    </CheckoutSimplifiedUIContext.Provider>
  );
};

export const useCheckoutSimplifiedUI = () => {
  const context = useContext(CheckoutSimplifiedUIContext);
  if (!context) {
    throw new Error(
      "useCheckoutSimplifiedUI must be used within a CheckoutSimplifiedUIProvider"
    );
  }
  return context;
};
